import { createSignal } from 'solid-js';
import { createStore, produce, unwrap } from 'solid-js/store';
import type { LibraryFile, LibraryRoom, ModalKind, OpenKind, Toast, ViewMode } from './types';
import { deleteDoc, loadDoc, loadLibrary, saveDoc, saveLibrary, uid } from './persistence';
import { createCollab, destroyCollab, getRemotePeers, type CollabContext, type RemotePeer } from './collab/yjs';

interface AppState {
  files: LibraryFile[];
  rooms: LibraryRoom[];
  openId: string | null;
  openKind: OpenKind;
  doc: string;
  mode: ViewMode;
  template: string;
  paletteOpen: boolean;
  modal: ModalKind;
  modalArg: string | null;
  toasts: Toast[];
  saveStatus: 'idle' | 'saving' | 'saved';
  hydrated: boolean;
}

const WELCOME = `---
title: Welcome
template: default
---

# Welcome to **md**

A markdown editor that doesn't suck.

## Quick tour

- Edit on the left, preview on the right
- **Cmd-K** — command palette
- **Cmd-N** — new file
- **Cmd-1 / 2 / 3** — edit / split / preview
- **Cmd-Alt-T** — format tables
- **Double-click** a block in the preview to edit inline

## Math

$$
\\int_0^\\infty e^{-x^2}\\,dx = \\frac{\\sqrt{\\pi}}{2}
$$

## Code

\`\`\`ts
function greet(name: string): string {
  return \`hello, \${name}\`;
}
\`\`\`

## Tables

| feature | status |
|---------|--------|
| editor  | ✓      |
| preview | ✓      |
| sync    | ✓      |

> Files auto-save to your browser's local storage. Use **Export** for a portable copy on disk.
`;

export const [state, setState] = createStore<AppState>({
  files: [],
  rooms: [],
  openId: null,
  openKind: null,
  doc: '',
  mode: 'split',
  template: 'default',
  paletteOpen: false,
  modal: null,
  modalArg: null,
  toasts: [],
  saveStatus: 'idle',
  hydrated: false,
});

// --- Collab signals (reactive for components) ---
const [collab, setCollab] = createSignal<CollabContext | null>(null);
const [peers, setPeers] = createSignal<RemotePeer[]>([]);
export type ConnStatus = 'local' | 'connecting' | 'connected' | 'offline';
const [connStatus, setConnStatus] = createSignal<ConnStatus>('local');
const [sidebarOpen, setSidebarOpen] = createSignal<boolean>(
  typeof window === 'undefined' ? true : window.innerWidth > 760,
);
const [notifCenterOpen, setNotifCenterOpen] = createSignal<boolean>(false);
export { collab, peers, connStatus, sidebarOpen, notifCenterOpen };
export const toggleSidebar = () => setSidebarOpen((v) => !v);
export const toggleNotifCenter = (open?: boolean) => setNotifCenterOpen((v) => open ?? !v);

let awarenessHandler: (() => void) | null = null;
let textObserver: (() => void) | null = null;
let statusHandler: ((e: any) => void) | null = null;
let connWatchdog: number | undefined;

async function tearDownCollab(): Promise<void> {
  const cur = collab();
  if (!cur) return;
  if (awarenessHandler) cur.awareness.off('change', awarenessHandler);
  if (textObserver) cur.ytext.unobserve(textObserver as any);
  if (statusHandler) (cur.provider as any).off('status', statusHandler);
  if (connWatchdog) { clearTimeout(connWatchdog); connWatchdog = undefined; }
  awarenessHandler = null;
  textObserver = null;
  statusHandler = null;
  destroyCollab(cur);
  setCollab(null);
  setPeers([]);
  setConnStatus('local');
}

// --- Hydration ---
export async function hydrate(): Promise<void> {
  const lib = await loadLibrary();
  let { files, rooms } = lib;
  let openId: string | null = null;
  let openKind: OpenKind = null;

  if (files.length === 0 && rooms.length === 0) {
    const id = uid();
    const file: LibraryFile = { id, title: 'welcome.md', updatedAt: Date.now(), size: WELCOME.length };
    files = [file];
    await saveDoc(id, WELCOME);
    await saveLibrary({ files, rooms });
    openId = id;
    openKind = 'file';
  } else {
    openId = files[0]?.id ?? rooms[0]?.id ?? null;
    openKind = files[0] ? 'file' : rooms[0] ? 'room' : null;
  }

  setState({ files, rooms, hydrated: true });
  if (openId && openKind) await openItem(openKind, openId);
  else setState({ openId: null, openKind: null, doc: '' });
}

// --- Persisting library ---
async function persistLibrary(): Promise<void> {
  await saveLibrary({ files: unwrap(state.files), rooms: unwrap(state.rooms) });
}

// --- Open / switch ---
export async function openItem(kind: 'file' | 'room', id: string, seed?: string): Promise<void> {
  if (state.openId === id && state.openKind === kind && collab()) return;

  // Save current file before switching
  if (state.openId && state.openKind === 'file') {
    await saveDoc(state.openId, state.doc);
  }

  await tearDownCollab();

  if (kind === 'room') {
    const room = state.rooms.find((r) => r.id === id);
    if (!room) return;
    const ctx = await createCollab(room.roomId, seed);

    const textCb = () => setState({ doc: ctx.ytext.toString() });
    ctx.ytext.observe(textCb);
    textObserver = textCb;

    const awCb = () => setPeers(getRemotePeers(ctx));
    ctx.awareness.on('change', awCb);
    awarenessHandler = awCb;
    setPeers(getRemotePeers(ctx));

    // Use provider.connected (the source of truth y-webrtc emits with the status event)
    setConnStatus((ctx.provider as any).connected ? 'connected' : 'connecting');
    const stCb = (e: any) => {
      setConnStatus(e?.connected ? 'connected' : 'offline');
    };
    (ctx.provider as any).on('status', stCb);
    statusHandler = stCb;
    // Watchdog: if still not connected in 6s, warn the user.
    connWatchdog = window.setTimeout(() => {
      if (!(ctx.provider as any).connected) {
        setConnStatus('offline');
        pushToast({
          severity: 'warn',
          title: 'Signaling unreachable',
          body: "Can't reach the signaling server, so other peers can't find this room yet. Your edits are saved locally and will sync the moment signaling comes back.",
          sticky: true,
        });
      }
    }, 6000);

    setCollab(ctx);
    setState({ openId: id, openKind: 'room', doc: ctx.ytext.toString(), saveStatus: 'saved' });
    setState(produce((s) => {
      const r = s.rooms.find((x) => x.id === id);
      if (r) r.unread = false;
    }));
    await persistLibrary();
  } else {
    const content = await loadDoc(id);
    setState({ openId: id, openKind: 'file', doc: content, saveStatus: 'saved' });
  }
}

// --- Doc edits ---
let saveTimer: number | undefined;
export function setDoc(next: string): void {
  setState({ doc: next });
  // In room mode, Yjs persistence handles saves (y-indexeddb) — skip our IDB autosave.
  if (state.openKind !== 'file') return;
  setState({ saveStatus: 'saving' });
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = window.setTimeout(async () => {
    if (state.openId && state.openKind === 'file') {
      await saveDoc(state.openId, next);
      const updatedAt = Date.now();
      const size = next.length;
      setState(produce((s) => {
        const f = s.files.find((x) => x.id === s.openId);
        if (f) { f.updatedAt = updatedAt; f.size = size; }
      }));
      await persistLibrary();
    }
    setState({ saveStatus: 'saved' });
  }, 400);
}

// --- Library mutations ---
export async function importFiles(): Promise<void> {
  const filesToImport: { name: string; text: string }[] = [];
  if ('showOpenFilePicker' in window) {
    try {
      const handles = await (window as any).showOpenFilePicker({
        multiple: true,
        types: [{ description: 'Markdown / text', accept: { 'text/markdown': ['.md', '.markdown', '.mdx', '.txt'] } }],
      });
      for (const h of handles) {
        const f = await h.getFile();
        filesToImport.push({ name: f.name, text: await f.text() });
      }
    } catch {
      return;
    }
  } else {
    await new Promise<void>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.accept = '.md,.markdown,.mdx,.txt';
      input.onchange = async () => {
        const list = Array.from(input.files ?? []);
        for (const f of list) filesToImport.push({ name: f.name, text: await f.text() });
        resolve();
      };
      input.oncancel = () => resolve();
      input.click();
    });
  }
  if (filesToImport.length === 0) return;
  let firstId: string | null = null;
  for (const { name, text } of filesToImport) {
    const id = uid();
    if (!firstId) firstId = id;
    const file: LibraryFile = { id, title: name, updatedAt: Date.now(), size: text.length };
    setState(produce((s) => { s.files.unshift(file); }));
    await saveDoc(id, text);
  }
  await persistLibrary();
  if (firstId) await openItem('file', firstId);
  pushToast({
    severity: 'success',
    title: `Imported ${filesToImport.length} file${filesToImport.length === 1 ? '' : 's'}`,
    body: filesToImport.map((f) => f.name).join(', '),
  });
}

export async function newFile(title = 'untitled.md'): Promise<string> {
  const id = uid();
  const file: LibraryFile = { id, title, updatedAt: Date.now(), size: 0 };
  setState(produce((s) => { s.files.unshift(file); }));
  const seed = `# ${title.replace(/\.md$/, '')}\n\n`;
  await saveDoc(id, seed);
  await persistLibrary();
  await openItem('file', id);
  return id;
}

export async function renameItem(id: string, title: string): Promise<void> {
  setState(produce((s) => {
    const f = s.files.find((x) => x.id === id);
    if (f) f.title = title;
    const r = s.rooms.find((x) => x.id === id);
    if (r) r.title = title;
  }));
  await persistLibrary();
}

export async function deleteFile(id: string): Promise<void> {
  setState(produce((s) => { s.files = s.files.filter((f) => f.id !== id); }));
  await deleteDoc(id);
  if (state.openId === id) {
    const remaining = state.files[0]?.id ?? state.rooms[0]?.id ?? null;
    const kind: OpenKind = remaining ? (state.files.some((f) => f.id === remaining) ? 'file' : 'room') : null;
    if (remaining && kind) await openItem(kind, remaining);
    else { await tearDownCollab(); setState({ openId: null, openKind: null, doc: '' }); }
  }
  await persistLibrary();
}

export async function leaveRoom(id: string): Promise<void> {
  if (state.openId === id) await tearDownCollab();
  setState(produce((s) => { s.rooms = s.rooms.filter((r) => r.id !== id); }));
  if (state.openId === id) {
    const remaining = state.files[0]?.id ?? state.rooms[0]?.id ?? null;
    const kind: OpenKind = remaining ? (state.files.some((f) => f.id === remaining) ? 'file' : 'room') : null;
    if (remaining && kind) await openItem(kind, remaining);
    else setState({ openId: null, openKind: null, doc: '' });
  }
  await persistLibrary();
}

export async function saveRoomToFileAndLeave(roomId: string): Promise<void> {
  const r = state.rooms.find((x) => x.id === roomId);
  if (!r) return;
  const content = (state.openId === roomId && state.openKind === 'room') ? state.doc : '';
  const id = uid();
  const file: LibraryFile = { id, title: `${r.title}.md`, updatedAt: Date.now(), size: content.length };
  setState(produce((s) => { s.files.unshift(file); }));
  await saveDoc(id, content);
  await persistLibrary();
  await leaveRoom(roomId);
  await openItem('file', id);
  pushToast({ severity: 'success', title: 'Saved to My files', body: `${file.title} added to your library.` });
}

export async function duplicateAsFile(roomId: string): Promise<void> {
  const r = state.rooms.find((x) => x.id === roomId);
  if (!r) return;
  // Snapshot current Y.Text content (if this is the active room) or empty
  const content = (state.openId === roomId && state.openKind === 'room') ? state.doc : '';
  const id = uid();
  const file: LibraryFile = { id, title: `${r.title} (snapshot).md`, updatedAt: Date.now(), size: content.length };
  setState(produce((s) => { s.files.unshift(file); }));
  await saveDoc(id, content);
  await persistLibrary();
  pushToast({ severity: 'success', title: 'Snapshot saved to My files' });
}

export async function shareAsRoom(fileId: string): Promise<string | null> {
  const f = state.files.find((x) => x.id === fileId);
  if (!f) return null;
  const seed = await loadDoc(fileId);
  const newRoomId = uid();
  const room: LibraryRoom = {
    id: fileId,
    title: f.title.replace(/\.md$/, ''),
    roomId: newRoomId,
    updatedAt: Date.now(),
    unread: false,
  };
  setState(produce((s) => {
    s.files = s.files.filter((x) => x.id !== fileId);
    s.rooms.unshift(room);
  }));
  await deleteDoc(fileId);
  await persistLibrary();
  await openItem('room', fileId, seed);
  return newRoomId;
}

export async function joinRoomByUrl(url: string): Promise<boolean> {
  const m = url.match(/#room=([\w-]+)/);
  if (!m) return false;
  const roomId = m[1];
  const existing = state.rooms.find((r) => r.roomId === roomId);
  if (existing) {
    await openItem('room', existing.id);
    return true;
  }
  const id = uid();
  const room: LibraryRoom = { id, title: 'Shared room', roomId, updatedAt: Date.now(), unread: false };
  setState(produce((s) => { s.rooms.unshift(room); }));
  await persistLibrary();
  await openItem('room', id);
  return true;
}

// --- Toasts ---
let toastCounter = 0;
const HISTORY_MAX = 50;
const NOTIF_KEY = 'md:notifications:v1';
export interface ToastHistoryEntry extends Toast { timestamp: number; }

function loadNotifications(): ToastHistoryEntry[] {
  try {
    const raw = localStorage.getItem(NOTIF_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function saveNotifications(list: ToastHistoryEntry[]): void {
  try {
    // Strip non-serializable fields (actions have functions)
    const safe = list.map((t) => ({
      id: t.id, severity: t.severity, title: t.title, body: t.body, timestamp: t.timestamp,
    }));
    localStorage.setItem(NOTIF_KEY, JSON.stringify(safe));
  } catch { /* */ }
}

const [toastHistory, setToastHistory] = createSignal<ToastHistoryEntry[]>(loadNotifications());
export { toastHistory };
// Seed counter so new IDs don't collide with persisted ones
toastCounter = toastHistory().reduce((m, t) => Math.max(m, t.id), 0);

export function pushToast(t: Omit<Toast, 'id'>): number {
  const id = ++toastCounter;
  const full: Toast = { ...t, id };
  setState(produce((s) => { s.toasts.push(full); }));
  const updated = [{ ...full, timestamp: Date.now() } as ToastHistoryEntry, ...toastHistory()].slice(0, HISTORY_MAX);
  setToastHistory(updated);
  saveNotifications(updated);
  if (!t.sticky) window.setTimeout(() => dismissToast(id), 4000);
  return id;
}
export function dismissToast(id: number): void {
  setState(produce((s) => { s.toasts = s.toasts.filter((x) => x.id !== id); }));
}
export function clearToastHistory(): void {
  setToastHistory([]);
  saveNotifications([]);
}

// --- Misc setters ---
export const setMode = (m: ViewMode) => setState({ mode: m });
export const setTemplate = (t: string) => setState({ template: t });
export const togglePalette = (open?: boolean) => setState({ paletteOpen: open ?? !state.paletteOpen });
export const showModal = (kind: ModalKind, arg: string | null = null) => setState({ modal: kind, modalArg: arg });
export const hideModal = () => setState({ modal: null, modalArg: null });

export function markRoomUnread(id: string): void {
  setState(produce((s) => {
    const r = s.rooms.find((x) => x.id === id);
    if (r) r.unread = true;
  }));
}
